import React, { useState, useEffect, useRef } from 'react';
import { Columns, Settings2, RefreshCw, Check } from 'lucide-react';

const ColumnPicker = ({ 
  columnsPool, 
  visibleColumns, 
  fixedColumns, 
  onSave, 
  onReset,
  maxColumns = 7
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempVisible, setTempVisible] = useState([...visibleColumns]);
  const popoverRef = useRef(null);

  // Sync temp state when popover opens or visibleColumns changes externally
  useEffect(() => {
    if (isOpen) {
      setTempVisible([...visibleColumns]);
    }
  }, [isOpen, visibleColumns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (accessor) => {
    if (fixedColumns.includes(accessor)) return; // Sabit olanlar değiştirilemez

    setTempVisible(prev => {
      const isCurrentlyVisible = prev.includes(accessor);
      
      if (isCurrentlyVisible) {
        return prev.filter(col => col !== accessor);
      } else {
        // Sabit kolonlar hariç kaç tane seçilmiş?
        const nonFixedSelectedCount = prev.filter(col => !fixedColumns.includes(col)).length;
        if (nonFixedSelectedCount >= maxColumns) {
          return prev; // limite ulaştı, ekleme yapma
        }
        return [...prev, accessor];
      }
    });
  };

  const handleApply = () => {
    onSave(tempVisible);
    setIsOpen(false);
  };

  const handleReset = () => {
    onReset();
    setIsOpen(false);
  };

  const nonFixedSelectedCount = tempVisible.filter(col => !fixedColumns.includes(col)).length;

  return (
    <div className="relative inline-block text-left" ref={popoverRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        title="Kolonları Düzenle"
      >
        <Columns className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
        <span className="hidden sm:inline">Kolonlar</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-64 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 border border-gray-100 dark:border-gray-700">
          <div className="p-3 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/80 rounded-t-md">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white flex items-center">
              <Settings2 className="w-4 h-4 mr-1.5 text-gray-500" />
              Görünüm Ayarları
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {nonFixedSelectedCount}/{maxColumns}
            </span>
          </div>

          <div className="max-h-60 overflow-y-auto p-2">
            {columnsPool.map((col) => {
              const isFixed = fixedColumns.includes(col.accessor);
              const isSelected = tempVisible.includes(col.accessor);
              const isDisabled = !isFixed && !isSelected && nonFixedSelectedCount >= maxColumns;

              return (
                <div 
                  key={col.accessor}
                  className={`flex items-center justify-between p-2 rounded-md mb-1 transition-colors ${
                    isFixed ? 'bg-gray-50 dark:bg-gray-700/50' : 
                    isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!isFixed && !isDisabled) handleToggle(col.accessor);
                  }}
                >
                  <span className={`text-sm ${isFixed ? 'text-gray-500 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                    {col.header}
                  </span>
                  
                  <div className="flex items-center">
                    {isFixed && (
                      <span className="text-[10px] uppercase font-bold text-gray-400 mr-2 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                        Sabit
                      </span>
                    )}
                    <div className={`w-5 h-5 border rounded flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800'
                    }`}>
                      {isSelected && <Check className="w-3.5 h-3.5 text-white stroke-[3]" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 rounded-b-md flex justify-between items-center gap-2">
            <button
              onClick={handleReset}
              className="flex items-center text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Varsayılan
            </button>
            <button
              onClick={handleApply}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md shadow-sm transition-colors"
            >
              Uygula
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnPicker;
